/**
 * Pure helpers for the warehouse planning tier map mode.
 *
 * The backend (WarehousePlanningService) is the source of truth for tier and
 * days-left. The front only maps tier -> color, builds the visibility ranking,
 * and aggregates group colors. No tier math happens here.
 */

export const WAREHOUSE_STATUS_ID = 3;
export const TRANSIT_FILL_COLOR = '#5A8DEE';

// Mirror of App\Enums\PlanningTier::color(). Backend also sends warehouseTierColor.
export const PLANNING_TIER_COLORS = {
  0: '#2e9e4f',
  1: '#e8b500',
  2: '#ef7d20',
  3: '#e23b3b',
};

export const PLANNING_MARKER_RADII = {
  default: 10,
  2: 12,
  3: 14,
};

export const PLANNING_PULSE_CONFIG = {
  2: { period: 3200, minRadius: 12, maxRadius: 16, color: PLANNING_TIER_COLORS[2] },
  3: { period: 1600, minRadius: 14, maxRadius: 32, color: PLANNING_TIER_COLORS[3] },
};

export const PLANNING_MAP_MODE = {
  LEGACY: 'legacy',
  PLANNING: 'planning',
};

/**
 * Whether an order carries a calculated planning tier (sender is configured).
 */
export function hasPlanningTier(order) {
  return order != null
    && order.warehouseTier !== undefined
    && order.warehouseTier !== null;
}

/** Human-readable hint for the next backend-calculated planning tier. */
export function getPlanningNextTierLabel(order) {
  if (order?.warehouseNextTier == null || order?.warehouseNextTierInDays == null) return null;

  const tier = Number(order.warehouseNextTier);
  const days = Number(order.warehouseNextTierInDays);
  if (!Number.isInteger(tier) || !Number.isInteger(days) || days < 1) return null;

  return days === 1
    ? `Za 1 dzień wskoczy do Tier ${tier}`
    : `Za ${days} dni wskoczy do Tier ${tier}`;
}

/** Number of planning requests sent to the customer, independent of failures. */
export function getPlanningRequestCount(order) {
  const count = Number(order?.deliveryConfirmationCount ?? 0);
  return Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
}

/**
 * Color for a warehouse order in planning mode. Falls back to the legacy
 * warehouse violet when the sender has no planning configuration.
 */
export function getPlanningTierColor(order, legacyColor = '#9370DB') {
  if (!hasPlanningTier(order)) return legacyColor;
  return order.warehouseTierColor || PLANNING_TIER_COLORS[order.warehouseTier] || legacyColor;
}

/**
 * Marker style descriptor (color + outline) for a warehouse order.
 * Urgent outline takes precedence over the "escalates tomorrow" outline.
 */
export function getPlanningMarkerStyle(order, mode = PLANNING_MAP_MODE.PLANNING) {
  if (mode !== PLANNING_MAP_MODE.PLANNING || !hasPlanningTier(order)) {
    return { color: getPlanningTierColor(order), outlineColor: '#ffffff', outlineWidth: 1.5 };
  }

  const color = getPlanningTierColor(order);

  if (order.isUrgent) {
    return { color, outlineColor: '#FF5B5C', outlineWidth: 3 };
  }
  if (order.escalatesTomorrow) {
    // Softer grey (not near-black) + thinner ring: a gentle "escalates tomorrow"
    // hint that no longer reads as a hard border on green/orange markers.
    return { color, outlineColor: '#6B7280', outlineWidth: 2 };
  }

  return { color, outlineColor: '#ffffff', outlineWidth: 1.5 };
}

/**
 * Urgency score for a tiered order — higher = more urgent. Pure, per-order.
 *
 * tier dominates (K=1000 safely exceeds the ~140-day days-left span, so tiers
 * never interleave); within a tier, fewer days-left ranks higher. Escalation is
 * deliberately NOT encoded here — it drives the marker outline, not visibility
 * ranking, and would only add fractional ties that muddy the day-slack cohorts.
 *
 * Untiered orders have no score (they are excluded from the visibility index and
 * only appear at full visibility).
 */
export const URGENCY_TIER_WEIGHT = 1000;

export function getUrgencyScore(order) {
  if (!hasPlanningTier(order)) return null;
  const daysLeft = order.warehouseDaysLeft ?? 0;
  return order.warehouseTier * URGENCY_TIER_WEIGHT - daysLeft;
}

/**
 * Build the ascending-sorted urgency index over a set of points/orders.
 * Only tiered orders contribute (untiered ones have no score). Computed once
 * per data change; the slider then does an O(1) quantile lookup into it.
 *
 * Accepts either raw orders or GeoJSON-ish points ({ properties: { order } }).
 */
function orderOf(pointOrOrder) {
  if (!pointOrOrder) return null;
  return pointOrOrder.properties?.order || pointOrOrder.order || pointOrOrder;
}

/**
 * Merge map points and selected route orders into one stable planning population.
 * In edit-route mode a clicked order can move from the map `points` collection
 * to `routeOrders`; if the visibility model is built from `points` only, the
 * slider recalculates after the first click and may reveal the next portion too
 * early. This helper keeps both sources in the population and deduplicates by
 * order id, so the cohort list changes only when the underlying route data
 * changes — not when a user merely selects one of the currently visible points.
 *
 * Accepts raw orders, GeoJSON-ish points ({ properties: { order } }) and route
 * elements ({ order }). Returns raw order objects.
 */
export function buildPlanningPopulation(...collections) {
  const byKey = new Map();
  collections.forEach((collection) => {
    (collection || []).forEach((item) => {
      const order = orderOf(item);
      if (!order) return;
      const key = order.id ?? order;
      byKey.set(key, order);
    });
  });
  return [...byKey.values()];
}

/**
 * Whether an order passes the slider given a precomputed cutoff.
 * Selected (on-route) orders are always visible. Untiered orders are visible
 * only at full visibility (cutoff -Infinity). Orders at/above the cutoff reveal
 * together (a whole day-slack cohort surfaces at once).
 */
export function isVisibleByCutoff(order, cutoff, isSelected = false) {
  if (isSelected) return true;
  const score = getUrgencyScore(order);
  if (score === null) return cutoff === Number.NEGATIVE_INFINITY;
  return score >= cutoff;
}

export const ALL_PLANNING_TIERS = [0, 1, 2, 3];

/**
 * Hybrid tier-stepped visibility model (operator spec 2026-06-23).
 *
 * The slider axis is split into one equal segment per tier PRESENT in the data,
 * ordered most-urgent first (left). Within a tier's segment the slider keeps
 * regulating visibility by days-left (the legacy intra-tier behaviour); the
 * segment BOUNDARIES are the clickable tier steps ("show this tier + every more
 * urgent one").
 *
 * score = tier * URGENCY_TIER_WEIGHT - daysLeft, so within one tier the score
 * range is driven purely by days-left:
 *   maxScore = tier*W - minDaysLeft  (the most urgent point of the tier)
 *   minScore = tier*W - maxDaysLeft  (the most slack point of the tier)
 *
 * Each tier also records `scores`: the DISTINCT score values actually present in
 * the data, sorted DESC (most urgent first). These are the real "cohorts" — the
 * only points where visibility can change. The slider steps through them evenly
 * inside the segment so dragging never crosses a dead zone (a stretch where the
 * interpolated cutoff moves but no point appears) and never skips a cohort
 * (operator feedback 2026-06-23). minScore/maxScore are kept as scores' ends.
 *
 * @returns {{tiers: Array<{tier:number,minScore:number,maxScore:number,scores:number[]}>}}
 *          tiers sorted DESC by tier number (most urgent first / left).
 */
export function buildTierVisibilityModel(points) {
  const byTier = new Map();
  (points || []).forEach((p) => {
    const order = orderOf(p);
    if (!hasPlanningTier(order)) return;
    const score = getUrgencyScore(order);
    if (score === null) return;
    const tier = order.warehouseTier;
    let cur = byTier.get(tier);
    if (!cur) {
      cur = { tier, scoreSet: new Set() };
      byTier.set(tier, cur);
    }
    cur.scoreSet.add(score);
  });
  const tiers = [...byTier.values()]
    .sort((a, b) => b.tier - a.tier)
    .map(({ tier, scoreSet }) => {
      const scores = [...scoreSet].sort((a, b) => b - a); // DESC, most urgent first
      return { tier, scores, maxScore: scores[0], minScore: scores[scores.length - 1] };
    });
  return { tiers };
}

// ---------------------------------------------------------------------------
// LEGACY percent-based helpers (kept for migration + backward compat)
// ---------------------------------------------------------------------------

/**
 * Cutoff score for a slider value (0..100) over the tier-stepped model.
 * @deprecated Use buildVisibilitySteps + cutoffForStep instead.
 * Kept for migration path only; Map.js no longer calls this.
 */
const TIER_BOUNDARY_EPS = 1e-6;

export function cutoffFromTierModel(model, visibilityPercent) {
  if (visibilityPercent >= 100) return Number.NEGATIVE_INFINITY;
  const tiers = model ? model.tiers : null;
  const k = tiers ? tiers.length : 0;
  if (k === 0) return Number.POSITIVE_INFINITY;

  const width = 100 / k;
  const raw = visibilityPercent / width;
  let segIdx = Math.floor(raw + TIER_BOUNDARY_EPS);
  if (segIdx < 0) segIdx = 0;
  let frac = raw - segIdx;
  if (frac < 0) frac = 0;
  if (frac < TIER_BOUNDARY_EPS && segIdx > 0) {
    segIdx -= 1;
    frac = 1;
  }
  if (segIdx > k - 1) {
    segIdx = k - 1;
    frac = 1;
  }
  const seg = tiers[segIdx];
  const scores = seg.scores && seg.scores.length ? seg.scores : [seg.maxScore];
  const n = scores.length;
  let j = Math.floor(frac * n);
  if (j > n - 1) j = n - 1;
  if (j < 0) j = 0;
  return scores[j];
}

/**
 * @deprecated Use indexForTierStep instead.
 */
export function valueForTierStep(model, tier) {
  const tiers = model ? model.tiers : null;
  const k = tiers ? tiers.length : 0;
  if (k === 0) return 100;
  const idx = tiers.findIndex((t) => t.tier === tier);
  if (idx === -1) return 100;
  const width = 100 / k;
  return (idx + 1) * width;
}

/**
 * @deprecated Use tierStepTicks instead.
 */
export function tierScaleTicks(model) {
  const tiers = model ? model.tiers : null;
  const k = tiers ? tiers.length : 0;
  if (k === 0) return [];
  const width = 100 / k;
  return tiers.map((t, idx) => ({
    tier: t.tier,
    color: PLANNING_TIER_COLORS[t.tier],
    labelPercent: (idx + 1) * width,
  }));
}

// ---------------------------------------------------------------------------
// NEW CORE: Discrete index-based visibility steps (Opcja 1 "steps-flatten")
// ---------------------------------------------------------------------------

/**
 * Flatten ALL cohorts of ALL tiers into a single ordered array of visibility
 * steps. The slider becomes a discrete <input type=range> whose value IS the
 * step index, eliminating dead zones completely: every index change maps to
 * exactly one new cohort being revealed.
 *
 * ORDERING — most urgent first (index 0 = left = most restrictive):
 *   T(highest) scores DESC → T(next) scores DESC → … → show-all sentinel
 *
 * CUTOFF SEMANTICS (consistent with isVisibleByCutoff):
 *   A step with cutoff C makes visible all orders whose score >= C.
 *   Index 0 has the highest cutoff (only the single most-urgent cohort visible).
 *   Each subsequent index LOWERS the cutoff, revealing one additional cohort.
 *   Cutoff is strictly monotonically decreasing across indices.
 *
 * LAST STEP — SHOW-ALL sentinel:
 *   { cutoff: Number.NEGATIVE_INFINITY, tier: null }
 *   This is the ONLY step that reveals untiered (legacy) orders, consistent
 *   with isVisibleByCutoff's rule: score===null visible only at cutoff===-Infinity.
 *   It is a SEPARATE step from the least-urgent tier's last cohort: even when
 *   the min score of T0 equals some integer > -Infinity, untiered orders still
 *   need this explicit sentinel to appear. Never conflate them.
 *
 * DEDUPLICATION:
 *   Distinct cutoffs only. If two tiers hypothetically produced the same score
 *   (impossible in practice because tier*URGENCY_TIER_WEIGHT separates them by
 *   1000, safely larger than the ~140-day daysLeft span), the second occurrence
 *   is silently dropped, preserving the strictly-descending invariant.
 *
 * EMPTY MODEL:
 *   No tiers → [{cutoff: -Infinity, tier: null}] — exactly one step, show-all.
 *
 * DEFINITION FOR dxzp71 (auto-advance, do NOT implement here — document only):
 *   "Points of step idx" = orders whose score === steps[idx].cutoff.
 *   These are the orders NEWLY revealed at this step (not cumulative). The
 *   auto-advance feature will use this definition to know which cohort just
 *   became visible and how long to highlight / dwell on it.
 *
 * @param {{ tiers: Array<{tier:number, scores:number[]}> }} model
 *   Output of buildTierVisibilityModel. tiers are sorted DESC (most urgent first);
 *   scores within each tier are also DESC.
 * @returns {Array<{cutoff:number, tier:number|null}>}
 *   Strictly descending cutoffs, length >= 1. Last element always has
 *   cutoff === Number.NEGATIVE_INFINITY and tier === null.
 */
// Default cap on slider steps PER TIER (subtask 869dxzmum follow-up). When a
// tier has more distinct cohorts than this, neighbouring cohorts are merged
// into bins so the slider stays short and low-granularity. A tier with fewer
// cohorts keeps 1 step per cohort (no merging), preserving the original
// "1 position = 1 cohort" behaviour the operator asked for when counts are low.
export const VISIBILITY_MAX_STEPS_PER_TIER = 3;

/**
 * Merge a DESC-sorted list of cohort scores into at most `maxBins` contiguous
 * bins, preserving order. Returns an array of bins, each bin an array of the
 * scores it absorbed (also DESC). The bin's CUTOFF is its LAST (smallest,
 * least-urgent) score, so `score >= cutoff` reveals the whole bin at once.
 *
 * <= maxBins scores  -> one bin per score (identity, no merging).
 * >  maxBins scores  -> split into `maxBins` near-equal contiguous chunks.
 *
 * @param {number[]} scoresDesc  Cohort scores, sorted DESC (most urgent first).
 * @param {number}   maxBins     Max bins to produce (>= 1).
 * @returns {number[][]} Bins in DESC order; each bin's scores also DESC.
 */
export function binScoresDesc(scoresDesc, maxBins) {
  const n = (scoresDesc || []).length;
  const k = Math.max(1, maxBins || 1);
  if (n === 0) return [];
  if (n <= k) return scoresDesc.map((s) => [s]); // low count -> keep 1:1
  // Distribute as EVENLY as possible into exactly k bins (not chunk-first):
  // for n=4,k=3 this yields sizes 2,1,1 (three bins) rather than 2,2 (two),
  // fully using the step budget. base = floor(n/k), the first `rem` bins get
  // one extra. Bins stay contiguous and DESC-ordered.
  const base = Math.floor(n / k);
  const rem = n % k;
  const bins = [];
  let i = 0;
  for (let b = 0; b < k; b++) {
    const size = base + (b < rem ? 1 : 0);
    bins.push(scoresDesc.slice(i, i + size));
    i += size;
  }
  return bins;
}

export function buildVisibilitySteps(model, options) {
  const tiers = model ? model.tiers : null;
  if (!tiers || tiers.length === 0) {
    return [{ cutoff: Number.NEGATIVE_INFINITY, tier: null }];
  }

  // When maxStepsPerTier is provided, cohorts within a tier are binned so the
  // slider stays short; when omitted, every distinct cohort is its own step
  // (original behaviour, kept for backward compatibility with existing callers
  // and tests). See binScoresDesc for the merge rule.
  // Only bin when a positive numeric cap is given; anything else (undefined,
  // 0, non-number) falls back to the original 1:1 behaviour.
  const rawCap = options ? options.maxStepsPerTier : undefined;
  const maxStepsPerTier = typeof rawCap === 'number' && rawCap > 0 ? rawCap : undefined;

  const steps = [];
  const seenCutoffs = new Set();

  // Walk tiers most-urgent first (model guarantees DESC order).
  // Within each tier, scores are also DESC (most urgent cohort first).
  for (const { tier, scores } of tiers) {
    // Each "cutoff" is either one score (no binning) or a bin's smallest score.
    const cutoffs = maxStepsPerTier
      ? binScoresDesc(scores, maxStepsPerTier).map((bin) => bin[bin.length - 1])
      : scores;
    for (const score of cutoffs) {
      if (!seenCutoffs.has(score)) {
        seenCutoffs.add(score);
        steps.push({ cutoff: score, tier });
      }
    }
  }

  // Append the show-all sentinel. -Infinity is always distinct from any real
  // score, so no dedup check needed.
  steps.push({ cutoff: Number.NEGATIVE_INFINITY, tier: null });

  return steps;
}

/**
 * O(1) cutoff lookup for a step index. Clamps to valid range.
 *
 * Replaces cutoffFromTierModel in the Map.js rendering path.
 *
 * @param {Array<{cutoff:number}>} steps  Output of buildVisibilitySteps.
 * @param {number}                 index  Current slider step index.
 * @returns {number}  The cutoff to pass to isVisibleByCutoff.
 */
export function cutoffForStep(steps, index) {
  if (!steps || steps.length === 0) return Number.NEGATIVE_INFINITY;
  const clamped = Math.max(0, Math.min(steps.length - 1, Math.round(index)));
  return steps[clamped].cutoff;
}

/**
 * Tick descriptors for the discrete slider — ONE tick per tier present,
 * placed at the index of that tier's LAST (least-urgent) cohort step.
 *
 * Semantics of "last cohort" placement:
 *   At this index the whole tier (and every more-urgent tier) is visible, but
 *   the first cohort of the NEXT (less-urgent) tier is NOT yet visible. This
 *   preserves the boundary rule from the old percent-based model: the right
 *   edge of a tier's group belongs to that tier, not to the neighbour.
 *
 * percent = index / (len - 1) * 100  (equal spacing; len = steps.length).
 * When len === 1 (empty model, only show-all) the single tick sits at 100%.
 *
 * Ticks are ordered most-urgent first (same order as steps / tiers).
 *
 * @param {Array<{cutoff:number, tier:number|null}>} steps
 * @returns {Array<{tier:number, color:string, index:number, percent:number}>}
 */
export function tierStepTicks(steps) {
  if (!steps || steps.length === 0) return [];

  const len = steps.length;
  // Map tier → index of its last cohort step.
  // We scan once and overwrite: the last occurrence of each tier in steps[]
  // is the least-urgent cohort of that tier (since steps are DESC).
  const lastIndexByTier = new Map();
  for (let i = 0; i < len; i++) {
    const { tier } = steps[i];
    if (tier !== null) {
      // Always overwrite — later index = less urgent cohort = what we want.
      lastIndexByTier.set(tier, i);
    }
  }

  // Build tick array in the same order as tiers appear in steps (most-urgent first).
  // Deduplicate tiers while preserving first-seen order.
  const seenTiers = [];
  const seenTierSet = new Set();
  for (const { tier } of steps) {
    if (tier !== null && !seenTierSet.has(tier)) {
      seenTierSet.add(tier);
      seenTiers.push(tier);
    }
  }

  return seenTiers.map((tier) => {
    const idx = lastIndexByTier.get(tier);
    const percent = len > 1 ? (idx / (len - 1)) * 100 : 100;
    return {
      tier,
      color: PLANNING_TIER_COLORS[tier],
      index: idx,
      percent,
    };
  });
}

// Minimum horizontal distance (px) between two tier label centres. A dot + "Tx"
// caption is ~30px wide; 40px leaves a small breathing gap so neighbours never
// visually touch.
export const TIER_LABEL_MIN_GAP_PX = 40;

/**
 * Push a sorted list of percent positions apart so no two are closer than
 * minGapPercent, while keeping their order and staying within [0, 100].
 *
 * Why this exists (subtask 869dxzmum): tier ticks are positioned at their real
 * cohort index (index/(stepCount-1)*100). When the least-urgent tier (T0) has
 * many cohorts, the step count is large and the low tiers (T3/T2/T1) — which
 * usually have only one or two cohorts — all land on adjacent low indices and
 * their captions overlap on the left. Widening the track cannot fix this: more
 * cohorts always re-compress the gap. Since there are at most 4 tiers, we can
 * instead nudge ONLY the labels apart (the slider thumb still moves by cohort).
 *
 * Algorithm: a forward pass pushes each label right until it clears the previous
 * one by minGapPercent; if that overflows past 100, a backward pass pulls the
 * tail back within bounds. With <= 4 labels and a sane gap this never saturates
 * (3 * gap << 100), so order and min-gap are both guaranteed. Labels that are
 * already far enough apart (e.g. a lone T0 on the right) are left untouched.
 *
 * @param {number[]} percents        Ascending percent positions (0..100).
 * @param {number}   minGapPercent    Minimum gap between adjacent labels, in %.
 * @returns {number[]} Adjusted percents, same length and order.
 */
export function spreadPercents(percents, minGapPercent) {
  const n = (percents || []).length;
  if (n <= 1) return (percents || []).slice();
  const gap = Math.max(0, minGapPercent || 0);
  const out = percents.slice();

  // Forward: ensure each label clears the previous by at least `gap`.
  for (let i = 1; i < n; i++) {
    if (out[i] < out[i - 1] + gap) out[i] = out[i - 1] + gap;
  }
  // If the forward pass ran past the right edge, pull the tail back in.
  if (out[n - 1] > 100) {
    out[n - 1] = 100;
    for (let i = n - 2; i >= 0; i--) {
      if (out[i] > out[i + 1] - gap) out[i] = out[i + 1] - gap;
    }
  }
  // Final clamp (defensive: only trips if labels genuinely cannot fit).
  for (let i = 0; i < n; i++) out[i] = Math.max(0, Math.min(100, out[i]));
  return out;
}

/**
 * Compute display percents for tier labels that never overlap, given the tick
 * descriptors from tierStepTicks and the rendered track width in px. Returns a
 * NEW array of ticks with an added `labelPercent` field; the original `percent`
 * (the true cohort position, used for the thumb) is preserved.
 *
 * @param {Array<{percent:number}>} ticks   tierStepTicks() output (ASC by percent).
 * @param {number} trackWidthPx             Rendered track width in px.
 * @returns {Array} ticks + labelPercent.
 */
export function withSpreadLabelPercents(ticks, trackWidthPx) {
  if (!ticks || ticks.length === 0) return [];
  const width = trackWidthPx > 0 ? trackWidthPx : 1;
  const minGapPercent = (TIER_LABEL_MIN_GAP_PX / width) * 100;
  const spread = spreadPercents(ticks.map((t) => t.percent), minGapPercent);
  return ticks.map((t, i) => ({ ...t, labelPercent: spread[i] }));
}

/**
 * Decide whether the visibility slider should auto-advance by exactly one step
 * after a selection change (subtask 869dxzp71).
 *
 * Client rule: the next slider position must reveal itself ONLY once every
 * point currently visible on the map has been selected — never after selecting
 * just one point out of several visible. So the trigger is "zero unselected
 * visible points remain", and the advance is always a single step.
 *
 * This is a PURE decision function. The React effect in Map.js supplies the
 * predicates (isVisible / isSelected) that close over the live cutoff and
 * selection set, so this helper stays testable without React.
 *
 * Loop safety: the effect re-runs after handleStepIndexChange bumps the index,
 * so this function MUST self-terminate. It does, via two guards:
 *   - rule 1: at the last step (show-all) there is nowhere to advance -> false.
 *   - rule 3: advance only if the IMMEDIATE next step reaches either an
 *     actionable unselected point, or a selected-only cohort that must be skipped
 *     to get to the next actionable portion. Transit-hidden unselected cohorts do
 *     not trigger the cascade. If everything reachable is already selected ->
 *     false.
 * Every advance therefore either lands on a step with unselected visible points
 * (stops on rule 2 next run), moves across a selected-only intermediate step, or
 * reaches the last step (stops on rule 1). No infinite loop.
 *
 * @param {Object}   args
 * @param {Array<{cutoff:number}>} args.steps         buildVisibilitySteps output.
 * @param {number}   args.currentIndex                Current slider step index.
 * @param {Array}    args.points                      Points or orders on the map.
 * @param {Function} args.isVisible  (order) => bool   Point is really visible now.
 * @param {Function} args.isSelected (order) => bool   Point is on the route.
 * @param {Function} [args.passesTransitFilter] (order) => bool  Step-independent
 *   filters (e.g. the awizacja/transit day filter). When supplied, rule 3 only
 *   counts a point as "would appear next" if it also passes this filter — so a
 *   cohort hidden by the transit filter can never trigger a cascade that reveals
 *   points the planner cannot actually act on. Defaults to always-true.
 * @returns {boolean} True to advance the slider by one step.
 */
export function shouldAutoAdvance({ steps, currentIndex, points, isVisible, isSelected, passesTransitFilter }) {
  const passesFilter = passesTransitFilter || (() => true);
  if (!steps || currentIndex >= steps.length - 1) return false; // rule 1: nowhere to go
  if (!points || points.length === 0) return false;

  const orders = points.map(orderOf).filter(Boolean);

  // rule 2: while any unselected point is still visible, keep the user here.
  const unselectedVisible = orders.filter((o) => isVisible(o) && !isSelected(o));
  if (unselectedVisible.length > 0) return false;

  // rule 3: inspect the IMMEDIATE next slider step. If it would reveal an
  // unselected point that passes step-independent filters, advance. If it would
  // reveal only an already-selected route order, also advance so the effect can
  // step across that selected-only intermediate. But a transit-hidden unselected
  // cohort must NOT trigger the cascade (operator/client decision from dxzp71).
  const currentCutoff = steps[currentIndex].cutoff;
  const nextCutoff = steps[currentIndex + 1].cutoff;
  const reachesAt = (order, cutoff) => {
    const score = getUrgencyScore(order);
    if (score === null) return cutoff === Number.NEGATIVE_INFINITY;
    return score >= cutoff;
  };
  const hasFutureActionableUnselected = orders.some((o) => {
    if (isSelected(o) || !passesFilter(o)) return false;
    return !isVisible(o);
  });
  if (!hasFutureActionableUnselected) return false;

  const nextStepHasReasonToAdvance = orders.some((o) => {
    const newlyReachedByNextStep = reachesAt(o, nextCutoff) && !reachesAt(o, currentCutoff);
    if (!newlyReachedByNextStep) return false;
    if (isSelected(o)) return true; // selected-only step: move across it
    return passesFilter(o); // actionable unselected step; hidden transit -> false
  });
  return nextStepHasReasonToAdvance;
}

/**
 * Step index a tier-click should navigate to: the index of the last cohort of
 * that tier (= the tier tick's index from tierStepTicks). At this index the
 * whole tier + every more-urgent tier is visible, but the next less-urgent tier
 * is not yet.
 *
 * Clicking the LEAST-urgent present tier does NOT navigate to show-all: it
 * reveals "that tier + pilniejsze", which is still one step before show-all
 * (untiered orders require the explicit -Infinity sentinel at the last index).
 *
 * Unknown / absent tier → last index (show-all).
 *
 * Replaces valueForTierStep in the Map.js click handler.
 *
 * @param {Array<{cutoff:number, tier:number|null}>} steps
 * @param {number} tier
 * @returns {number}  Step index to pass to handleStepIndexChange.
 */
export function indexForTierStep(steps, tier) {
  if (!steps || steps.length === 0) return 0;
  const lastIndex = steps.length - 1;
  if (tier === null || tier === undefined) return lastIndex;

  // Find the last occurrence of this tier in steps (= least-urgent cohort).
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].tier === tier) return i;
  }
  return lastIndex; // tier not present → show-all
}

// ---------------------------------------------------------------------------
// PERSISTENCE HELPERS — semantic anchor [0,1] instead of raw step index
// ---------------------------------------------------------------------------

/**
 * Storage keys for the new anchor-based persistence and the legacy value key.
 * Exported so Map.js can reference them without string duplication.
 */
export const PLANNING_VISIBILITY_ANCHOR_KEY = 'route-map-planning-visibility-anchor';
export const PLANNING_VISIBILITY_LEGACY_KEY = 'route-map-planning-visibility-value';

/**
 * Derive a semantic anchor (fraction in [0,1]) from a step index.
 *   0.0 = most restrictive (leftmost, index 0)
 *   1.0 = show-all (rightmost, last index)
 *
 * Stored in localStorage so the planner's chosen cohort survives route changes.
 * When the step array length changes (new route, different population) the anchor
 * is re-mapped to the nearest step via stepIndexForAnchor — a fraction of 0.5
 * maps to the middle cohort regardless of how many cohorts exist.
 *
 * @param {Array<{cutoff:number}>} steps
 * @param {number} index
 * @returns {number}  Value in [0,1].
 */
export function anchorForStepIndex(steps, index) {
  if (!steps || steps.length <= 1) return 1.0;
  const clamped = Math.max(0, Math.min(steps.length - 1, index));
  return clamped / (steps.length - 1);
}

/**
 * Reconstruct the nearest step index from a stored semantic anchor.
 *
 * @param {Array<{cutoff:number}>} steps
 * @param {number} anchor  Value in [0,1] (from anchorForStepIndex or migration).
 * @returns {number}  Clamped step index.
 */
export function stepIndexForAnchor(steps, anchor) {
  if (!steps || steps.length === 0) return 0;
  if (steps.length === 1) return 0;
  const raw = anchor * (steps.length - 1);
  return Math.max(0, Math.min(steps.length - 1, Math.round(raw)));
}

/**
 * Load the visibility anchor from localStorage, handling migration from the
 * legacy percent-based key.
 *
 * Migration rule (one-time, non-destructive):
 *   Old key `route-map-planning-visibility-value` stored a percent 0..100.
 *   anchor = percent / 100 maps perfectly: 100 (show-all) → 1.0 (show-all).
 *
 * Default (no key present): 1.0 (show-all, consistent with the old default of 100).
 *
 * @returns {number}  Anchor in [0,1].
 */
export function loadVisibilityAnchor() {
  try {
    const newRaw = window.localStorage.getItem(PLANNING_VISIBILITY_ANCHOR_KEY);
    if (newRaw !== null && String(newRaw).trim() !== '') {
      const n = Number(newRaw);
      if (!Number.isNaN(n)) return Math.max(0, Math.min(1, n));
    }
    // Migration: attempt to read the old percent-based key.
    const legacyRaw = window.localStorage.getItem(PLANNING_VISIBILITY_LEGACY_KEY);
    if (legacyRaw !== null && String(legacyRaw).trim() !== '') {
      const pct = Number(legacyRaw);
      if (!Number.isNaN(pct)) return Math.max(0, Math.min(1, pct / 100));
    }
  } catch (e) {
    // localStorage not available (SSR / private-mode block).
  }
  return 1.0; // default: show-all
}

/**
 * Count planning points per tier from a set of points.
 * Only points with a calculated tier are counted.
 */
export function countPlanningTiers(points) {
  const counts = { 0: 0, 1: 0, 2: 0, 3: 0 };
  (points || []).forEach((order) => {
    if (hasPlanningTier(order)) {
      counts[order.warehouseTier] = (counts[order.warehouseTier] || 0) + 1;
    }
  });
  return counts;
}

/**
 * Aggregate color/tier for a group of orders at the same address:
 * the group takes the most urgent (highest) tier present.
 */
export function getGroupTier(orders) {
  let worst = null;
  (orders || []).forEach((order) => {
    if (hasPlanningTier(order)) {
      worst = worst === null ? order.warehouseTier : Math.max(worst, order.warehouseTier);
    }
  });
  return worst;
}

export function getGroupColor(orders, legacyColor = '#9370DB') {
  const tier = getGroupTier(orders);
  if (tier === null) return legacyColor;
  return PLANNING_TIER_COLORS[tier] || legacyColor;
}

/** Pixel size boost for a collapsed group bubble based on its worst-child tier (T3→6, T2→3, others→0). */
export function getGroupSizeBoost(orders) {
  const tier = getGroupTier(orders);
  if (tier === 3) return 6;
  if (tier === 2) return 3;
  return 0;
}

/**
 * Highest urgency score in a group (used only for diagnostics/sorting; the slider
 * visibility for groups is decided per-member via isVisibleByCutoff in Map.js so
 * singles and groups never diverge).
 */
export function getGroupScore(orders) {
  return (orders || []).reduce((max, order) => {
    const score = getUrgencyScore(order);
    return score === null ? max : Math.max(max, score);
  }, 0);
}

// Slider sizing constants (subtask 869dxzmum). After the discrete-step rework
// the tier ticks sit at real cohort indices (index/(stepCount-1)*100), so the
// gap between adjacent tier labels shrinks as the number of cohorts grows.
// A widened but FIXED track only postpones the overlap; past ~10 cohorts two
// tiers landing on neighbouring indices collide again. Instead we scale the
// track width with the step count while guaranteeing a minimum pixel gap PER
// STEP — since adjacent tiers differ by at least one step, this makes label
// collisions structurally impossible across the normal range.
export const VISIBILITY_SLIDER_MIN_WIDTH = 300; // floor: few cohorts stay compact
export const VISIBILITY_SLIDER_MAX_WIDTH = 560; // cap: protect the map layout
export const VISIBILITY_SLIDER_PX_PER_STEP = 44; // >= widest tier caption (~30px) + breathing room

/**
 * Pixel width for the discrete visibility slider track given the number of
 * steps. Grows linearly with the step count (min gap per step) but is clamped
 * to a floor (so a single-cohort slider is not tiny) and a cap (so a very long
 * route does not overflow the map). Because neighbouring tier ticks are always
 * at least one step apart, VISIBILITY_SLIDER_PX_PER_STEP is also the minimum
 * guaranteed distance between two adjacent tier labels — no overlap.
 *
 * @param {number} stepCount  visibilitySteps.length (>= 1).
 * @returns {number} Track width in px.
 */
export function visibilitySliderWidth(stepCount) {
  const segments = Math.max(0, (stepCount || 1) - 1); // gaps between positions
  // Ideal = one guaranteed gap per segment (+ the 16px edge inset the ticks live
  // in). The floor keeps a few-cohort slider compact instead of ballooning; the
  // cap protects the map layout. So: stay at the floor while gaps are already
  // generous, grow to hold PX_PER_STEP once they would tighten, stop at the cap.
  const ideal = segments * VISIBILITY_SLIDER_PX_PER_STEP + 16;
  return Math.min(
    VISIBILITY_SLIDER_MAX_WIDTH,
    Math.max(VISIBILITY_SLIDER_MIN_WIDTH, ideal),
  );
}
